import { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, Bike, Calendar, Store } from 'lucide-react';
import { AVATAR_BG, AVATAR_FG } from '../constants/avatar';

const PERIODS = [
  { id: 'all',    label: 'Toutes' },
  { id: 'today',  label: "Aujourd'hui" },
  { id: 'week',   label: 'Semaine' },
  { id: 'month',  label: 'Mois' },
  { id: 'custom', label: 'Période' },
];

function getDateRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }
  if (period === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }
  if (period === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start, end: now };
  }
  if (period === 'custom' && customFrom && customTo) {
    const start = new Date(customFrom); start.setHours(0, 0, 0, 0);
    const end = new Date(customTo); end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  return null;
}

export const DEFAULT_FILTERS = {
  search: '', period: 'all', customFrom: '', customTo: '', driverId: '', storeId: '',
};

export function applyOrderFilters(list, filters, searchFields = []) {
  let result = list;

  if (filters.search?.trim()) {
    const q = filters.search.toLowerCase();
    result = result.filter(o =>
      searchFields.some(f => o[f]?.toString().toLowerCase().includes(q))
    );
  }

  if (filters.period && filters.period !== 'all') {
    const range = getDateRange(filters.period, filters.customFrom, filters.customTo);
    if (range) {
      result = result.filter(o => {
        const d = new Date(o.created_at);
        return d >= range.start && d <= range.end;
      });
    }
  }

  if (filters.driverId) {
    result = result.filter(o => String(o.driver_id) === filters.driverId);
  }

  if (filters.storeId) {
    result = result.filter(o => o.store === filters.storeId);
  }

  return result;
}

export default function OrderFilters({
  filters,
  onChange,
  onReset,
  drivers = [],
  stores = [],
  showDriver = false,
  showStore = false,
  searchPlaceholder = 'Rechercher...',
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  const activeCount = [
    filters.period && filters.period !== 'all' ? filters.period : '',
    filters.driverId,
    filters.storeId,
  ].filter(Boolean).length;

  const selectedDriver  = drivers.find(d => String(d.id) === filters.driverId) || null;
  const currentPeriod   = PERIODS.find(p => p.id === (filters.period || 'all')) || PERIODS[0];
  const periodActive    = filters.period && filters.period !== 'all';

  const pillStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: '5px',
    padding: '7px 11px', borderRadius: '10px',
    border: `1.5px solid ${active ? '#173b78' : '#e2e8f0'}`,
    background: active ? '#f0f4ff' : 'white',
    color: active ? '#173b78' : '#6b7280',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
    flexShrink: 0, whiteSpace: 'nowrap',
  });

  const dropdownStyle = {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0,
    background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200,
    minWidth: '180px', overflow: 'hidden', maxHeight: '260px', overflowY: 'auto',
  };

  const dropdownItemStyle = (active, last) => ({
    width: '100%', padding: '10px 14px',
    background: active ? '#f0f4ff' : 'white',
    border: 'none', borderBottom: last ? 'none' : '1px solid #f1f5f9',
    cursor: 'pointer', textAlign: 'left',
    fontSize: '13px', color: active ? '#173b78' : '#374151',
    fontWeight: active ? 700 : 400,
  });

  return (
    <div ref={ref} style={{ marginBottom: '14px' }}>

      {/* Barre de recherche */}
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <Search size={14} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
        <input
          value={filters.search}
          onChange={e => onChange({ search: e.target.value })}
          placeholder={searchPlaceholder}
          style={{ width: '100%', padding: '11px 36px', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: 'white', color: '#1a1a2e' }}
          onFocus={e => e.target.style.borderColor = '#173b78'}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
        {filters.search && (
          <button onClick={() => onChange({ search: '' })} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '3px', borderRadius: '6px', display: 'flex' }}>
            <X size={13} />
          </button>
        )}
      </div>

      {/* Ligne chips filtres */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>

        {/* Période */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button onClick={() => setOpenDropdown(openDropdown === 'period' ? null : 'period')} style={pillStyle(periodActive)}>
            <Calendar size={11} />
            {currentPeriod.label}
            <ChevronDown size={11} style={{ transform: openDropdown === 'period' ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </button>
          {openDropdown === 'period' && (
            <div style={dropdownStyle}>
              {PERIODS.map((p, i) => (
                <button key={p.id}
                  onClick={() => {
                    onChange({ period: p.id, ...(p.id !== 'custom' ? { customFrom: '', customTo: '' } : {}) });
                    if (p.id !== 'custom') setOpenDropdown(null);
                  }}
                  style={dropdownItemStyle((filters.period || 'all') === p.id, i === PERIODS.length - 1)}
                  onMouseEnter={e => { if ((filters.period || 'all') !== p.id) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = (filters.period || 'all') === p.id ? '#f0f4ff' : 'white'; }}>
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Livreur */}
        {showDriver && drivers.length > 0 && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setOpenDropdown(openDropdown === 'driver' ? null : 'driver')} style={pillStyle(!!filters.driverId)}>
              {selectedDriver ? (
                <>
                  <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontSize: '7px', fontWeight: 700 }}>{selectedDriver.initials}</div>
                  {selectedDriver.name}
                </>
              ) : <><Bike size={11} />Livreur</>}
              <ChevronDown size={11} style={{ transform: openDropdown === 'driver' ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {openDropdown === 'driver' && (
              // Largeur calée sur le nom le plus long : sans ça, la liste prend
              // la largeur du bouton et coupe les noms longs sur deux lignes.
              <div style={{ ...dropdownStyle, minWidth: '220px', width: 'max-content', maxWidth: 'calc(100vw - 32px)' }}>
                {filters.driverId && (
                  <button onClick={() => { onChange({ driverId: '' }); setOpenDropdown(null); }}
                    style={{ width: '100%', padding: '10px 14px', background: 'white', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left', fontSize: '12px', color: '#be123c', fontWeight: 600 }}>
                    ✕ Effacer
                  </button>
                )}
                {drivers.map((d, i) => (
                  <button key={d.id}
                    onClick={() => { onChange({ driverId: String(d.id) }); setOpenDropdown(null); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: filters.driverId === String(d.id) ? '#f0f4ff' : 'white', border: 'none', borderBottom: i < drivers.length - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer' }}
                    onMouseEnter={e => { if (filters.driverId !== String(d.id)) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = filters.driverId === String(d.id) ? '#f0f4ff' : 'white'; }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{d.initials}</div>
                    <span style={{ fontSize: '13px', color: '#0f172a', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Boutique */}
        {showStore && stores.length > 0 && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setOpenDropdown(openDropdown === 'store' ? null : 'store')} style={pillStyle(!!filters.storeId)}>
              <Store size={11} />
              {filters.storeId || 'Boutique'}
              <ChevronDown size={11} style={{ transform: openDropdown === 'store' ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {openDropdown === 'store' && (
              <div style={{ ...dropdownStyle, minWidth: '200px' }}>
                {filters.storeId && (
                  <button onClick={() => { onChange({ storeId: '' }); setOpenDropdown(null); }}
                    style={{ width: '100%', padding: '10px 14px', background: 'white', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left', fontSize: '12px', color: '#be123c', fontWeight: 600 }}>
                    ✕ Effacer
                  </button>
                )}
                {stores.map((s, i) => (
                  <button key={s}
                    onClick={() => { onChange({ storeId: s }); setOpenDropdown(null); }}
                    style={dropdownItemStyle(filters.storeId === s, i === stores.length - 1)}
                    onMouseEnter={e => { if (filters.storeId !== s) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = filters.storeId === s ? '#f0f4ff' : 'white'; }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reset */}
        {(activeCount > 0 || filters.search) && (
          <button onClick={onReset}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 11px', borderRadius: '10px', border: '1.5px solid #fecdd3', background: '#fff1f2', color: '#be123c', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            <X size={11} />
            {activeCount > 0 ? `${activeCount} filtre${activeCount > 1 ? 's' : ''}` : 'Effacer'}
          </button>
        )}
      </div>

      {/* Plage personnalisée */}
      {filters.period === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <input type="date" value={filters.customFrom}
            onChange={e => onChange({ customFrom: e.target.value })}
            style={{ flex: 1, padding: '7px 9px', border: '1.5px solid #173b78', borderRadius: '10px', fontSize: '12px', outline: 'none', color: '#173b78', fontWeight: 600, background: '#f0f4ff', boxSizing: 'border-box' }}
          />
          <span style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>→</span>
          <input type="date" value={filters.customTo}
            onChange={e => onChange({ customTo: e.target.value })}
            style={{ flex: 1, padding: '7px 9px', border: '1.5px solid #173b78', borderRadius: '10px', fontSize: '12px', outline: 'none', color: '#173b78', fontWeight: 600, background: '#f0f4ff', boxSizing: 'border-box' }}
          />
        </div>
      )}
    </div>
  );
}
